import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useNavigationFilter } from '../../hooks/useNavigationFilter';
import { NavTierRenderer } from './NavTierRenderer';
import { NavHomeButtonComponent } from './NavHomeButton';
import { COLLAPSED_WIDTH, EXPANDED_WIDTH, NAV_TRANSITION, NAV_BG } from './navConstants';
import OptumLogoCollapsed from '../../assets/optum-logo-collapsed.png';
import OptumLogoExpanded from '../../assets/optum-logo-expanded.png';

interface VerticalNavigationProps {
  onHoverChange?: (isHovered: boolean) => void;
}

export const VerticalNavigation: React.FC<VerticalNavigationProps> = ({ onHoverChange }) => {
  const [isHovered, setIsHovered] = useState(true);
  const navRef = useRef<HTMLElement | null>(null);

  const filteredNav = useNavigationFilter();

  // Separate home from rest for special rendering
  const homeItem = filteredNav.find(item => item.id === 'home');
  const navItems = filteredNav.filter(item => item.id !== 'home');

  useEffect(() => {
    onHoverChange?.(isHovered);
  }, [isHovered, onHoverChange]);

  // Body push: update CSS variable on hover
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-nav-width',
      isHovered ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
    );
  }, [isHovered]);

  const handleNavFocusCapture = () => {
    setIsHovered(true);
  };

  const focusNextOutsideNav = () => {
    const navElement = navRef.current;
    if (!navElement) return;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusableElements = Array.from(
      document.querySelectorAll<HTMLElement>(focusableSelector),
    ).filter(element => {
      if (element.hasAttribute('disabled')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    const activeElement = document.activeElement as HTMLElement | null;
    const activeIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;

    for (let index = activeIndex + 1; index < focusableElements.length; index += 1) {
      const candidate = focusableElements[index];
      if (!navElement.contains(candidate)) {
        candidate.focus();
        return;
      }
    }

    const fallback = focusableElements.find(element => !navElement.contains(element));
    fallback?.focus();
  };

  const handleNavKeyDownCapture = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;

    e.preventDefault();
    e.stopPropagation();
    setIsHovered(false);

    window.setTimeout(() => {
      focusNextOutsideNav();
    }, 0);
  };

  return (
    <Box
      component="nav"
      ref={navRef}
      aria-label="Primary"
      onFocusCapture={handleNavFocusCapture}
      onKeyDownCapture={handleNavKeyDownCapture}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        pt: 1,
        height: '100vh',
        width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        transition: `width ${NAV_TRANSITION}`,
        backgroundColor: NAV_BG,
        color: '#1f2b3a',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          position: 'relative',
        }}
      >
        <img
          src={isHovered ? OptumLogoExpanded : OptumLogoCollapsed}
          alt="Optum Logo"
          style={{
            height: 32,
            objectFit: 'contain',
            transition: `opacity 200ms`,
          }}
        />
      </Box>

      {/* Scroll container */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden', 
          pt: 1, 
          px: '12px',
          // Hide scrollbar
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Edge
          },
        }}
      >
        {/* Tier 1 items (Home rendered as the first item of the same list) */}
        <NavTierRenderer
          items={navItems}
          tier={1}
          isNavOpen={isHovered}
          leadingItem={
            homeItem && <NavHomeButtonComponent item={homeItem} isNavOpen={isHovered} />
          }
        />
      </Box>
    </Box>
  );
};
